import { ArrowUpRightIcon, CornersOut, X } from '@phosphor-icons/react'
import { DialogClose } from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { PageInfo } from '@/src/@types/models'
import { BardMapAddress as BardMapAddressType } from '@/src/@types/statamic'
import Button from '@/src/components/core/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/src/components/core/Dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/src/components/core/Popover'
import JsonLd from '@/src/components/elements/JsonLd'
import Map from '@/src/components/elements/Map'
import Marker, {
  getIconBasedOnLocationType,
} from '@/src/components/elements/Marker'
import { normalizeInternational, swissFormat } from '@/src/lib/phone'
import siteInfo, { getBusinessType } from '@/src/lib/siteInfo'

type AddressGroup = {
  type: string
  label: string
  addresses: BardMapAddressType['all_addresses']
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  lesson: 'Treffpunkt Fahrlektion',
  pickup: 'Treffpunkt Fahrlektion',
  'parking-lot': 'Treffpunkt Fahrlektion',
  'pgs-pickup': 'Treffpunkt PGS',
  course: 'Kurslokal',
  'exam-office': 'Prüfungsamt',
  wab: 'WAB Kurslokal',
}

const LOCATION_TYPE_ICONS: Record<string, string> = {
  lesson: "bg-[url('/img/markers/lesson_marker.svg')]",
  pickup: "bg-[url('/img/markers/lesson_marker.svg')]",
  'parking-lot': "bg-[url('/img/markers/lesson_marker.svg')]",
  course: "bg-[url('/img/markers/course_marker.svg')]",
  'exam-office': "bg-[url('/img/markers/exam_marker.svg')]",
  wab: "bg-[url('/img/markers/wab_marker.svg')]",
  'pgs-pickup': "bg-[url('/img/markers/pgs_marker.svg')]",
}

export default function BardMapAddress({
  addresses,
  all_addresses,
  google_rating,
  pageInfo,
}: BardMapAddressType & {
  pageInfo?: PageInfo
}) {
  const selectedAddresses = all_addresses
    .filter((addr) =>
      addresses.map((addr) => addr.value).includes(String(addr.id))
    )
    .sort((a, b) => {
      const aIndex = Object.keys(LOCATION_TYPE_LABELS).indexOf(
        a.type || 'default'
      )
      const bIndex = Object.keys(LOCATION_TYPE_LABELS).indexOf(
        b.type || 'default'
      )
      return aIndex - bIndex
    })

  const addressGroups = useMemo(() => {
    const groups: Record<string, AddressGroup> = {}

    selectedAddresses.forEach((address) => {
      const type = address.type || 'default'
      if (!groups[type]) {
        groups[type] = {
          type,
          label: LOCATION_TYPE_LABELS[type] || 'Standort',
          addresses: [],
        }
      }
      groups[type].addresses.push(address)
    })

    return Object.values(groups)
  }, [selectedAddresses])

  if (!google_rating.longitude || !google_rating.latitude) return null

  return (
    <div>
      <div className="hidden md:block">
        <Desktop
          selectedAddresses={selectedAddresses}
          google_rating={google_rating}
          all_addresses={all_addresses}
          groupedAddresses={addressGroups}
        />
      </div>
      <div className="md:hidden">
        <Mobile
          selectedAddresses={selectedAddresses}
          google_rating={google_rating}
          all_addresses={all_addresses}
        />
      </div>
      <div className="mt-8 md:mt-6">
        <p>Du hast Fragen? Kontaktiere uns.</p>
        <div className="mt-2 flex">
          {google_rating.email && (
            <Link
              href={`mailto:${google_rating.email}`}
              className="font-bold text-purple-600"
            >
              {google_rating.email}
            </Link>
          )}
          {google_rating.email && google_rating.phone && (
            <div className="mx-3 border-l border-gray-300" />
          )}
          {google_rating.phone && (
            <Link
              href={`tel:${normalizeInternational(google_rating.phone)}`}
              className="font-bold text-purple-600"
            >
              {swissFormat(google_rating.phone)}
            </Link>
          )}
        </div>
      </div>
      {pageInfo && (
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': getBusinessType(pageInfo.fullUrl),
            name: siteInfo.orgName,
            description: siteInfo.locationName(google_rating.place),
            address: {
              '@type': 'PostalAddress',
              streetAddress: google_rating.street,
              addressLocality: google_rating.place,
              postalCode: google_rating.zip,
            },
            aggregateRating:
              google_rating.rating && google_rating.rating > 0
                ? {
                    '@type': 'AggregateRating',
                    ratingValue: google_rating.rating,
                    ratingCount: google_rating.rating_count,
                    bestRating: 5,
                    worstRating: 1,
                  }
                : undefined,
            geo:
              google_rating.latitude && google_rating.longitude
                ? {
                    '@type': 'GeoCoordinates',
                    latitude: google_rating.latitude,
                    longitude: google_rating.longitude,
                  }
                : undefined,
            // priceRange: courses[0] ? `${courses[0].price} CHF` : undefined,
            // hasMap: `https://g.page/${gmbShortname}`,
            openingHours: ['Mo,Tu,We,Th,Fr 09:00-17:00', 'Sa 09:00-12:00'],
            telephone: google_rating.phone,
            logo: pageInfo.settings.logo?.permalink,
            url: pageInfo.fullUrl,
            image: pageInfo.socialImage
              ? [pageInfo.socialImage?.permalink]
              : undefined,
          }}
        />
      )}
    </div>
  )
}

const Desktop = ({
  selectedAddresses,
  google_rating,
  all_addresses,
  groupedAddresses,
}: {
  selectedAddresses: BardMapAddressType['all_addresses']
  google_rating: BardMapAddressType['google_rating']
  all_addresses: BardMapAddressType['all_addresses']
  groupedAddresses: AddressGroup[]
}) => {
  const [hoveredAddressId, setHoveredAddressId] = useState<
    string | number | null
  >(null)

  return (
    <div className="grid grid-cols-2 gap-12">
      <DialogMap addresses={all_addresses} google_rating={google_rating}>
        <div className="relative flex h-123 w-full overflow-hidden rounded-xl border border-gray-200">
          <div className="size-full">
            <Map
              markers={selectedAddresses.map((addr) => [
                addr.longitude,
                addr.latitude,
              ])}
              interactive={false}
              className="h-full w-full"
            >
              {selectedAddresses.map((address, index) => (
                <Marker
                  key={index}
                  position={{
                    lat: address.latitude,
                    lng: address.longitude,
                  }}
                  icon={getIconBasedOnLocationType(address.type)}
                  size="lg"
                  focused={hoveredAddressId === address.id}
                  zIndex={selectedAddresses.length - index}
                >
                  {`
                      <div>
                        <h4 class="leading-5 font-black text-base">${address.name}</h4>
                        <a class="mt-1 block text-purple-600 text-sm" href="https://www.google.com/maps/place/${
                          address.place
                        }+${address.street.replace(
                          ' ',
                          '+'
                        )}" target="_blank" rel="noopener noreferrer">
                          ${address.street}, ${address.place}
                        </a>
                      </div>
                    `}
                </Marker>
              ))}
            </Map>
            <div className="absolute top-2 right-2">
              <DialogTrigger>
                <Button
                  as="div"
                  version="white-gray"
                  size="icon-small"
                  icon={<CornersOut weight="bold" />}
                />
              </DialogTrigger>
            </div>
          </div>
        </div>
      </DialogMap>
      <div className="flex max-h-123 flex-col">
        <p className="mt-5 mb-4 text-xl font-black">
          Standorte in {google_rating.place}
        </p>
        <div className="space-y-3 overflow-y-auto">
          {groupedAddresses.map((group) => (
            <div key={group.label}>
              <p className="text-sm font-bold text-gray-600">{group.label}</p>
              <div className="mt-1">
                {group.addresses.map((address, addrIndex) => (
                  <DesktopAddress
                    key={addrIndex}
                    address={address}
                    onMouseEnter={() => setHoveredAddressId(address.id)}
                    onMouseLeave={() => setHoveredAddressId(null)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const Mobile = ({
  selectedAddresses,
  google_rating,
  all_addresses,
}: {
  selectedAddresses: BardMapAddressType['all_addresses']
  google_rating: BardMapAddressType['google_rating']
  all_addresses: BardMapAddressType['all_addresses']
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
  })

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <>
      <p className="text-xl font-black">Standorte in {google_rating.place}</p>
      <DialogMap addresses={all_addresses} google_rating={google_rating}>
        <div className="relative mt-5 flex h-56 w-full overflow-hidden rounded-xl border border-gray-200">
          <div className="size-full">
            <Map
              markers={selectedAddresses.map((addr) => [
                addr.longitude,
                addr.latitude,
              ])}
              interactive={false}
              className="h-full w-full"
            >
              {selectedAddresses.map((address, index) => (
                <Marker
                  key={index}
                  position={{
                    lat: address.latitude,
                    lng: address.longitude,
                  }}
                  icon={getIconBasedOnLocationType(address.type)}
                  size="lg"
                  focused={index === selectedIndex}
                  zIndex={selectedAddresses.length - index}
                >
                  {`
                      <div>
                        <h4 class="leading-5 font-black text-base">${address.name}</h4>
                        <a class="mt-1 block text-purple-600 text-sm" href="https://www.google.com/maps/place/${
                          address.place
                        }+${address.street.replace(
                          ' ',
                          '+'
                        )}" target="_blank" rel="noopener noreferrer">
                          ${address.street}, ${address.place}
                        </a>
                      </div>
                    `}
                </Marker>
              ))}
            </Map>
            <div className="absolute top-2 right-2">
              <DialogTrigger>
                <Button
                  as="div"
                  version="white-gray"
                  size="icon-small"
                  icon={<CornersOut weight="bold" />}
                />
              </DialogTrigger>
            </div>
          </div>
        </div>
      </DialogMap>
      <div ref={emblaRef} className="-mx-5 mt-6 overflow-hidden">
        <ul className="flex select-none">
          {selectedAddresses.map((address) => (
            <li
              key={address.id}
              className="ml-5 min-w-0 flex-[0_0_calc(100%-40px-20px)] last:mr-5"
            >
              <MobileAddress address={address} />
            </li>
          ))}
        </ul>
      </div>
      {selectedAddresses.length > 1 && (
        <div className="mt-4 flex justify-center space-x-1">
          {selectedAddresses.map((_, index) => (
            <div
              key={index}
              className={clsx('rounded-full transition-all', {
                'size-2 bg-gray-200': selectedIndex !== index,
                'h-2 w-3 bg-gray-800': selectedIndex === index,
              })}
            />
          ))}
        </div>
      )}
    </>
  )
}

const DesktopAddress = ({
  address,
  onMouseEnter,
  onMouseLeave,
}: {
  address: BardMapAddressType['all_addresses'][0]
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}) => {
  const iconClass = LOCATION_TYPE_ICONS[address.type || 'default']
  return (
    <Link
      href={`https://www.google.com/maps/place/${
        address.place
      }+${address.street.replace(' ', '+')}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center justify-between rounded-lg p-2 pr-3 hover:bg-gray-100"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center space-x-3">
        <div
          className={clsx(
            'h-10 w-8 shrink-0 bg-contain bg-center bg-no-repeat',
            iconClass
          )}
        />
        <div>
          <p className="font-bold">{address.name}</p>
          <p className="text-gray-600 group-hover:underline">
            {address.street}, {address.zip} {address.place}
          </p>
        </div>
      </div>
      <div className="hidden rounded-full bg-white p-2 group-hover:block">
        <ArrowUpRightIcon size={20} className="text-gray-800" />
      </div>
    </Link>
  )
}

const MobileAddress = ({
  address,
}: {
  address: BardMapAddressType['all_addresses'][0]
}) => {
  const iconClass = LOCATION_TYPE_ICONS[address.type || 'default']
  return (
    <Link
      href={`https://www.google.com/maps/place/${
        address.place
      }+${address.street.replace(' ', '+')}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex h-full items-center space-x-3 rounded-lg bg-gray-100 px-4 py-3"
    >
      <div
        className={clsx(
          'h-10 w-8 shrink-0 bg-contain bg-center bg-no-repeat',
          iconClass
        )}
      />
      <div>
        <p className="text-xs font-bold text-gray-600">
          {LOCATION_TYPE_LABELS[address.type || 'default']}
        </p>
        <p className="font-bold">{address.name}</p>
        <p className="text-gray-600 underline">
          {address.street}, {address.zip} {address.place}
        </p>
      </div>
    </Link>
  )
}

const DialogMap = ({
  children,
  addresses,
  google_rating,
}: {
  children?: ReactNode
  addresses: BardMapAddressType['all_addresses']
  google_rating: BardMapAddressType['google_rating']
}) => {
  return (
    <Dialog>
      {children}
      <DialogContent
        withPositioning={false}
        mapSafe
        className="inset-x-0 top-21 bottom-0 w-auto overflow-hidden rounded-t-xl md:inset-x-10 md:top-9.5 md:bottom-10 md:rounded-2xl"
      >
        <DialogHeader
          title={`BLINK-Standorte in ${google_rating.place}`}
          className="md:hidden"
        />
        <div className="relative size-full">
          <Map
            center={{
              lat: google_rating.latitude!,
              lng: google_rating.longitude!,
            }}
            zoomAfterClick={false}
            className="size-full"
          >
            {addresses.map((address, index) => (
              <Marker
                key={index}
                position={{
                  lat: address.latitude,
                  lng: address.longitude,
                }}
                icon={getIconBasedOnLocationType(address.type)}
                size="lg"
              >
                {`
                      <div>
                        <h4 class="leading-5 font-black text-base">${address.name}</h4>
                        <a class="mt-1 block text-purple-600 text-sm" href="https://www.google.com/maps/place/${
                          address.place
                        }+${address.street.replace(
                          ' ',
                          '+'
                        )}" target="_blank" rel="noopener noreferrer">
                          ${address.street}, ${address.place}
                        </a>
                      </div>
                    `}
              </Marker>
            ))}
          </Map>
          <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-20 bg-linear-to-b from-black/50 to-black/0 md:block" />
          <div className="absolute inset-x-0 top-0 hidden items-center justify-between px-6 pt-6 md:flex">
            <p className="text-4xl font-black text-white text-shadow-md">
              BLINK-Standorte in {google_rating.place}
            </p>
            <DialogClose asChild>
              <Button
                version="white-gray"
                size="icon-small"
                icon={<X weight="bold" />}
              />
            </DialogClose>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                version="white-gray"
                className="group absolute bottom-8 left-5 z-50 md:bottom-6 md:left-6"
              >
                Legende{' '}
                <span className="hidden group-data-[state=closed]:inline">
                  einblenden
                </span>
                <span className="hidden group-data-[state=open]:inline">
                  ausblenden
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              color="light"
              arrow={false}
              side="top"
              padding={false}
              shadow
              align="start"
              sideOffset={12}
              className="space-y-2 rounded-xl border-none py-3 pr-6 pl-3"
            >
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 32 40"
                  width="30"
                  height="40"
                  className="drop-shadow-sm"
                >
                  <path
                    fill="#6a29ec"
                    stroke="#fff"
                    d="M16 .5a15.67 15.67 0 0 1 10.958 4.465 15.1 15.1 0 0 1 4.542 10.75c0 6.545-3.545 12.331-7.227 16.568-3.672 4.225-7.422 6.841-7.72 7.046a.98.98 0 0 1-1.106 0c-.298-.205-4.048-2.821-7.72-7.046C4.046 28.046.5 22.26.5 15.715a15.1 15.1 0 0 1 4.542-10.75A15.67 15.67 0 0 1 16 .5Z"
                  />
                  <path
                    fill="#fff"
                    d="M24.75 14.125h-.844l-2.17-4.883a1.25 1.25 0 0 0-1.142-.742h-9.188a1.25 1.25 0 0 0-1.142.742l-2.17 4.883H7.25a.625.625 0 1 0 0 1.25h.625v6.25a1.25 1.25 0 0 0 1.25 1.25H11a1.25 1.25 0 0 0 1.25-1.25V21h7.5v.625a1.25 1.25 0 0 0 1.25 1.25h1.875a1.25 1.25 0 0 0 1.25-1.25v-6.25h.625a.624.624 0 1 0 0-1.25m-12.5 3.75h-1.875a.625.625 0 1 1 0-1.25h1.875a.624.624 0 1 1 0 1.25m9.375 0H19.75a.624.624 0 1 1 0-1.25h1.875a.624.624 0 1 1 0 1.25m-12.163-3.75 1.944-4.375h9.188l1.944 4.375z"
                  />
                </svg>
                <p>Treffpunkt Fahrlektionen</p>
              </div>
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 32 40"
                  width="32"
                  height="40"
                  className="drop-shadow-sm"
                >
                  <path
                    fill="#fbbf24"
                    stroke="#fff"
                    d="M16 .5a15.67 15.67 0 0 1 10.958 4.465 15.1 15.1 0 0 1 4.542 10.75c0 6.545-3.545 12.331-7.227 16.568-3.672 4.225-7.422 6.841-7.72 7.046a.98.98 0 0 1-1.106 0c-.298-.205-4.048-2.821-7.72-7.046C4.046 28.046.5 22.26.5 15.715a15.1 15.1 0 0 1 4.542-10.75A15.67 15.67 0 0 1 16 .5Z"
                  />
                  <path
                    fill="#78350f"
                    d="M19.75 22.19q.647-.255 1.25-.603v3.163a.624.624 0 1 1-1.25 0zm.919-6.908-4.375-2.334a.625.625 0 0 0-.588 1.104L19.36 16l1.328-.708zm5-2.334-9.375-5a.63.63 0 0 0-.588 0l-9.375 5a.625.625 0 0 0 0 1.104L8.5 15.209v3.782c0 .307.112.604.317.832 1.023 1.14 3.316 3.052 7.183 3.052 1.282.01 2.554-.222 3.75-.684v-5.982l-.39-.21L16 17.792l-6.576-3.51-1.471-.781L16 9.208l8.047 4.292-1.468.781h-.004l-1.888 1.011a.63.63 0 0 1 .313.542v5.753a9 9 0 0 0 2.183-1.764c.204-.228.317-.525.317-.832V15.21l2.169-1.157a.625.625 0 0 0 0-1.104"
                  />
                </svg>
                <p>Kurslokal</p>
              </div>
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 32 40"
                  width="32"
                  height="40"
                  className="drop-shadow-sm"
                >
                  <path
                    fill="#f9a8d4"
                    stroke="#fff"
                    d="M16 .5a15.67 15.67 0 0 1 10.958 4.465 15.1 15.1 0 0 1 4.542 10.75c0 6.545-3.545 12.331-7.227 16.568-3.672 4.225-7.422 6.841-7.72 7.046a.98.98 0 0 1-1.106 0c-.298-.205-4.048-2.821-7.72-7.046C4.046 28.046.5 22.26.5 15.715a15.1 15.1 0 0 1 4.542-10.75A15.67 15.67 0 0 1 16 .5Z"
                  />
                  <path
                    fill="#831843"
                    d="M24.281 12.26a1.87 1.87 0 0 0-1.406-.635H18.5v-1.25a3.125 3.125 0 0 0-3.125-3.125.63.63 0 0 0-.56.345l-2.95 5.905H8.5a1.25 1.25 0 0 0-1.25 1.25v6.875a1.25 1.25 0 0 0 1.25 1.25h13.438a1.875 1.875 0 0 0 1.86-1.64l.938-7.5a1.87 1.87 0 0 0-.455-1.476M8.5 14.75h3.125v6.875H8.5z"
                  />
                </svg>
                <p>Prüfungszentrum</p>
              </div>
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 32 40"
                  width="32"
                  height="40"
                  className="drop-shadow-sm"
                >
                  <path
                    fill="#34d399"
                    stroke="#fff"
                    d="M16 .5a15.67 15.67 0 0 1 10.958 4.465 15.1 15.1 0 0 1 4.542 10.75c0 6.545-3.545 12.331-7.227 16.568-3.672 4.225-7.422 6.841-7.72 7.046a.98.98 0 0 1-1.106 0c-.298-.205-4.048-2.821-7.72-7.046C4.046 28.046.5 22.26.5 15.715a15.1 15.1 0 0 1 4.542-10.75A15.67 15.67 0 0 1 16 .5Z"
                  />
                  <path
                    fill="#064e3b"
                    d="M24.125 22.25h-1.43l-4.71-13.535a1.25 1.25 0 0 0-1.18-.84h-1.61a1.25 1.25 0 0 0-1.18.84L9.304 22.25h-1.43a.625.625 0 1 0 0 1.25h16.25a.624.624 0 1 0 0-1.25m-10.67-8.125h5.09l1.304 3.75h-7.698z"
                  />
                </svg>
                <p>WAB Standort</p>
              </div>
              <div className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 32 40"
                  width="32"
                  height="40"
                  className="drop-shadow-sm"
                >
                  <path
                    fill="#b5c1ff"
                    stroke="#fff"
                    d="M16 .5a15.67 15.67 0 0 1 10.958 4.465 15.1 15.1 0 0 1 4.542 10.75c0 6.545-3.545 12.331-7.227 16.568-3.672 4.225-7.422 6.841-7.72 7.046a.98.98 0 0 1-1.106 0c-.298-.205-4.048-2.821-7.72-7.046C4.046 28.046.5 22.26.5 15.715a15.1 15.1 0 0 1 4.542-10.75A15.67 15.67 0 0 1 16 .5Z"
                  />
                  <path
                    fill="#0d0b3f"
                    d="M22.875 15.375q-.26 0-.516.043l-.454-1.183q.478-.11.97-.11a.624.624 0 1 0 0-1.25h-1.494l-1.047-2.724a.625.625 0 0 0-.584-.401h-2.5a.625.625 0 1 0 0 1.25h2.071l.721 1.875h-2.167c-1.445 0-2.617.337-3.388.973a1.25 1.25 0 0 1-1.31.162c-.826-.376-5.725-2.353-5.765-2.364a.625.625 0 0 0-.39 1.187s4.335 1.714 5.197 2.573a4.35 4.35 0 0 1 1.237 2.469h-1.268a3.125 3.125 0 1 0 0 1.25h4.071a2.49 2.49 0 0 0 2.402-1.805 4.38 4.38 0 0 1 2.077-2.634l.455 1.182a3.125 3.125 0 1 0 1.682-.493m-13.75 3.75h1.767a1.875 1.875 0 1 1 0-1.25H9.125a.625.625 0 1 0 0 1.25m13.75 1.25a1.875 1.875 0 0 1-1.217-3.3l.633 1.65a.625.625 0 0 0 1.168-.45l-.635-1.65h.051a1.875 1.875 0 1 1 0 3.75"
                  />
                </svg>
                <p>Treffpunkt PGS</p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </DialogContent>
    </Dialog>
  )
}
